import { DatePickerPreset, zonedStartOfToday } from 'shared-ui';
import { SpotInterface } from 'types';
import { months } from 'utils/constants';
import { getMonthDateRange, newUTCDate } from 'utils/helpers';

export function getLastMonthRange(today: Date): { startDate: Date; endDate: Date } {
    let month = today.getMonth();
    let year = today.getFullYear();
    if (month === 0) {
        month = 12;
        year -= 1;
    }
    return getMonthDateRange(month, year, true);
}

export function getSpotSelectOptions(orgSpots: Record<string, SpotInterface>) {
    const orgSpotsKeys = Object.keys(orgSpots);
    const selectOptions =
        orgSpotsKeys.length > 1
            ? [
                  {
                      value: '',
                      label: 'All Properties',
                  },
                  ...orgSpotsKeys.map((pk) => ({ value: pk, label: orgSpots[pk].name })),
              ]
            : undefined;

    return selectOptions;
}

export function getDateRangeKey(startDate: string, endDate: string) {
    return `${startDate}-${endDate}`;
}

export const ENTERPRISE_TOOLTIP_TEXT =
    'Enterprise includes non-parking revenue as well as partnerships with car share companies, EV charging, Fleet storage, and other businesses.';

export function getPreviousMonth(today: Date) {
    const currentMonth = today.getMonth();
    const index = currentMonth === 0 ? 11 : currentMonth - 1;
    return months[index];
}

// Returns "Month Year" (e.g. "April 2026") only when the selected date range falls
// within a single calendar month; returns null for multi-month ranges since there's no
// single month to label.
export function getSingleMonthLabel(startDate: string, endDate: string): string | null {
    const start = new Date(`${startDate}T00:00:00Z`);
    const end = new Date(`${endDate}T00:00:00Z`);
    if (start.getUTCFullYear() !== end.getUTCFullYear() || start.getUTCMonth() !== end.getUTCMonth()) {
        return null;
    }
    return `${months[start.getUTCMonth()]} ${start.getUTCFullYear()}`;
}

function addDays(value: Date, days: number): Date {
    const next = new Date(value);
    next.setDate(next.getDate() + days);
    return next;
}

// Owners report on whole months, so the income statement drops the day-scale presets
// (today / yesterday / last 7 days) and offers month- and year-scale ranges instead.
export const INCOME_STATEMENT_PRESETS: DatePickerPreset<'range'>[] = [
    {
        id: 'this-month',
        label: 'This Month',
        getRange: () => {
            const to = zonedStartOfToday();
            return { from: new Date(to.getFullYear(), to.getMonth(), 1), to };
        },
    },
    {
        id: 'last-month',
        label: 'Last Month',
        getRange: () => {
            const { startDate, endDate } = getLastMonthRange(new Date());
            return { from: startDate, to: endDate };
        },
    },
    {
        id: 'last-30-days',
        label: 'Last 30 Days',
        getRange: () => {
            const to = zonedStartOfToday();
            return { from: addDays(to, -29), to };
        },
    },
    {
        id: 'year-to-date',
        label: 'Year to Date',
        getRange: () => {
            const to = zonedStartOfToday();
            return { from: new Date(to.getFullYear(), 0, 1), to };
        },
    },
    {
        id: 'last-12-months',
        label: 'Last 12 Months',
        getRange: () => {
            const to = zonedStartOfToday();
            return { from: addDays(new Date(to.getFullYear(), to.getMonth() - 12, to.getDate()), 1), to };
        },
    },
    {
        id: 'last-year',
        label: 'Last Year',
        getRange: () => {
            const year = new Date().getFullYear() - 1;
            return { from: new Date(year, 0, 1), to: new Date(year, 11, 31) };
        },
    },
    {
        id: 'all-time',
        label: 'All Time',
        getRange: (_timeZone, options) => ({
            from: options?.minDate ?? new Date(2000, 0, 1),
            to: zonedStartOfToday(),
        }),
    },
    {
        id: 'custom',
        label: 'Custom',
        getRange: () => ({}),
    },
];

// The org has no income before its first property went live.
export function getOrgLaunchDate(orgSpots: Record<string, SpotInterface>): Date | undefined {
    const launchDates = Object.values(orgSpots)
        .map((spot) => spot.launch_date)
        .filter((launchDate): launchDate is string => !!launchDate)
        // `launch_date` is an ISO timestamp; keep the calendar day it names instead of
        // letting the viewer's zone shift it a day either way.
        .map((launchDate) => newUTCDate(launchDate.slice(0, 10)));

    if (launchDates.length === 0) return undefined;
    return launchDates.reduce((earliest, launchDate) => (launchDate < earliest ? launchDate : earliest));
}
